// ============================================================
// گفت‌وگوی صوتی پیوسته
//
// حالت تک‌دستوری (voice.ts) سر جایش می‌ماند: دکمه را می‌زنی، یک
// جمله می‌گویی، تمام. این فایل حالت دوم است — مثل حالت صوتی
// ChatGPT: یک بار روشن می‌شود و تا وقتی خاموشش نکنی، گوش می‌دهد،
// جواب می‌دهد و دوباره گوش می‌دهد.
//
// سه مشکلی که حالت پیوسته‌ی قبلی را از کار انداخته بود، و اینجا
// هر کدام جداگانه حل شده‌اند:
//
// ۱) «کروم نشست را می‌بندد.» درست است و جنگیدن با آن فایده ندارد.
//    به‌جایش هر بار که بسته شد، خودمان نشست تازه باز می‌کنیم. از
//    دید کاربر یکپارچه است.
//
// ۲) «موتور بعد از چند نشست گیر می‌کند.» ریشه‌اش نمونه‌ی کهنه بود.
//    هر نشست با نمونه‌ی تازه شروع می‌شود و هندلرهای نمونه‌ی قبلی
//    جدا می‌شوند، پس onend دیرهنگام نمی‌تواند وضعیت را خراب کند.
//
// ۳) «صدای بلندگو وارد میکروفون می‌شود.» این واقعی است و با
//    نرم‌افزار حل نمی‌شود. راه‌حل نیم‌دوطرفه (half-duplex) است:
//    تا وقتی دستیار حرف می‌زند میکروفون بسته است و درست بعد از
//    تمام شدن جمله باز می‌شود. دقیقاً کاری که حالت صوتی ChatGPT
//    هم می‌کند.
//
// قاعده‌ی طلایی که نباید شکسته شود: وضعیت را حدس نزنید. running
// فقط در onstart و onend مقدار می‌گیرد، چون onerror همیشه قبل از
// onend می‌آید.
// ============================================================

import { isSpeaking } from "./speak";

export const CONVERSATION_BUILD = "havir-convo-1";

/** بعد از این مدت سکوت، چیزی که شنیده شده ارسال می‌شود. */
const SILENCE_MS = 900;
const MOBILE_SILENCE_MS = 1800;
/** فاصله‌ی باز کردن نشست بعدی — نفس کشیدن موتور تشخیص. */
const RESTART_MS = 350;
/** تشخیص گفتار موبایل بعد از سکوت زود بسته می‌شود؛ فاصله‌ی بیشتر از
 * قطع‌و‌وصل تند نشان دادن میکروفن جلوگیری می‌کند. */
const MOBILE_RESTART_MS = 1400;
/** اگر پشت سر هم شکست خورد، دست می‌کشیم تا حلقه‌ی بی‌پایان نشود. */
const MAX_FAILURES = 5;

export type ConversationState = "off" | "listening" | "thinking" | "speaking";

export interface ConversationHandlers {
    /** یک جمله‌ی کامل از کاربر */
    onUtterance: (text: string) => void;
    /** متن در حال شکل‌گیری، فقط برای نمایش */
    onPartial?: (text: string) => void;
    onState?: (state: ConversationState) => void;
    onError?: (message: string) => void;
}

const getRecognitionCtor = (): any => {
    if (typeof window === "undefined") return null;
    const w = window as any;
    return w.SpeechRecognition || w.webkitSpeechRecognition || null;
};

const isMobileBrowser = (): boolean =>
    typeof navigator !== "undefined" && /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

export const isConversationSupported = (): boolean => !!getRecognitionCtor();

/**
 * حلقه‌ی گفت‌وگو.
 *
 * عمداً کلاس ساده است نه هوک: طول عمرش به رندرهای ری‌اکت وصل نیست
 * و باید بتواند از داخل افکت‌ها و هندلرها بدون ساخته شدن دوباره
 * صدا زده شود.
 */
export class ConversationLoop {
    private handlers: ConversationHandlers;
    private recognition: any = null;

    /** کاربر حالت گفت‌وگو را روشن کرده است */
    private active = false;
    /** موتور تشخیص همین حالا باز است (فقط از onstart/onend) */
    private running = false;
    /** منتظریم جواب بیاید یا خوانده شود — در این مدت گوش نمی‌دهیم */
    private paused = false;

    private transcript = "";
    private silenceTimer: any = null;
    private restartTimer: any = null;
    private nextRestartMs = RESTART_MS;
    private failures = 0;
    private state: ConversationState = "off";

    constructor(handlers: ConversationHandlers) {
        this.handlers = handlers;
    }

    /** هندلرها هر رندر تازه‌اند؛ حلقه نباید برای این دوباره ساخته شود. */
    setHandlers(handlers: ConversationHandlers): void {
        this.handlers = handlers;
    }

    isActive(): boolean {
        return this.active;
    }

    // ---------- کنترل بیرونی ----------

    start(): void {
        if (this.active) return;
        this.active = true;
        this.paused = false;
        this.nextRestartMs = RESTART_MS;
        this.failures = 0;
        this.openSession();
    }

    /** شروع حلقه در حالت مکث؛ برای پخش پیام آغازین پیش از روشن‌کردن میکروفن. */
    startPaused(state: ConversationState = "speaking"): void {
        if (this.active) return;
        this.active = true;
        this.paused = true;
        this.failures = 0;
        this.clearTimers();
        this.closeSession();
        this.emit(state);
    }

    stop(): void {
        this.active = false;
        this.paused = false;
        this.clearTimers();
        this.closeSession();
        this.emit("off");
    }

    /**
     * «ساکت باش، دارم فکر می‌کنم / حرف می‌زنم.»
     * میکروفون بسته می‌شود تا صدای خود دستیار وارد آن نشود.
     */
    pause(state: ConversationState = "thinking"): void {
        if (!this.active) return;
        this.paused = true;
        this.clearTimers();
        this.closeSession();
        this.emit(state);
    }

    /** «حرفم تمام شد، دوباره گوش بده.» */
    resume(): void {
        if (!this.active) return;
        this.paused = false;
        this.failures = 0;
        this.scheduleRestart();
    }

    /**
     * مثل resume، ولی اگر دستیار دارد حرف می‌زند دست نمی‌زند.
     *
     * برای مسیرهایی است که پاسخ صوتی ندارند — پاک کردن گفتگو، خطا،
     * پیامی که وسط کار دیگری آمده. بدون این، حلقه در حالت مکث گیر
     * می‌کرد و کاربر فکر می‌کرد گفت‌وگو روشن است ولی میکروفون برای
     * همیشه بسته می‌ماند. با آن هم نباید میکروفون را وسط جمله‌ی
     * دستیار باز کند، وگرنه صدای خودش را می‌شنود.
     */
    resumeIfIdle(): void {
        if (!this.active) return;
        if (this.state === "speaking") return;
        this.resume();
    }

    // ---------- داخلی ----------

    private emit(state: ConversationState): void {
        this.state = state;
        if (this.handlers.onState) this.handlers.onState(state);
    }

    private clearTimers(): void {
        if (this.silenceTimer) {
            clearTimeout(this.silenceTimer);
            this.silenceTimer = null;
        }
        if (this.restartTimer) {
            clearTimeout(this.restartTimer);
            this.restartTimer = null;
        }
    }

    private closeSession(): void {
        const instance = this.recognition;
        this.recognition = null;
        this.running = false;
        if (!instance) return;

        // هندلرها جدا می‌شوند تا onend دیرهنگامِ این نمونه، نشست
        // بعدی را خراب نکند.
        instance.onstart = null;
        instance.onresult = null;
        instance.onerror = null;
        instance.onend = null;
        try {
            instance.abort();
        } catch {
            /* نادیده */
        }
    }

    private scheduleRestart(): void {
        if (!this.active || this.paused) return;
        if (this.restartTimer) return;

        this.restartTimer = setTimeout(() => {
            this.restartTimer = null;
            this.openSession();
        }, this.nextRestartMs);
    }

    /** چیزی که تا الان شنیده‌ایم را به بالا می‌دهد. */
    private flush(): void {
        const text = this.transcript.trim();
        this.transcript = "";
        if (!text) return;

        this.pause("thinking");
        this.handlers.onUtterance(text);
    }

    private openSession(): void {
        if (!this.active || this.paused || this.running) return;

        /*
          محافظ دوم: تا دستیار حرف می‌زند، میکروفون باز نمی‌شود.

          در حالت عادی pause("speaking") و resume() این را تضمین
          می‌کنند، ولی آن زنجیره به درست بودن یک تایمر وابسته است. یک
          بار همان تایمر بدموقع فعال شد و میکروفون وسط جمله‌ی دستیار
          باز شد؛ دستیار صدای خودش را شنید و تشخیص گفتار بی‌دقت شد.

          این شرط آن اتفاق را از ریشه غیرممکن می‌کند: اگر صدا در حال
          پخش است، کمی بعد دوباره تلاش می‌کنیم.
        */
        if (isSpeaking()) {
            this.scheduleRestart();
            return;
        }

        const SR = getRecognitionCtor();
        if (!SR) return;

        this.closeSession();

        const instance = new SR();
        instance.lang = "fa-IR";
        // continuous=true را می‌خواهیم ولی به آن تکیه نمی‌کنیم؛
        // کروم هر طور شده بعد از سکوت نشست را می‌بندد.
        instance.continuous = true;
        instance.interimResults = true;
        instance.maxAlternatives = 1;

        instance.onstart = () => {
            this.running = true;
            this.failures = 0;
            this.nextRestartMs = RESTART_MS;
            this.emit("listening");
        };

        instance.onresult = (event: any) => {
            let finalText = "";
            let interim = "";

            for (let i = event.resultIndex; i < event.results.length; i++) {
                const result = event.results[i];
                if (result.isFinal) finalText += result[0].transcript;
                else interim += result[0].transcript;
            }

            if (finalText) this.transcript += finalText;

            const preview = (this.transcript + " " + interim).trim();
            if (preview && this.handlers.onPartial) this.handlers.onPartial(preview);

            // هر صدایی تایمر سکوت را عقب می‌اندازد. ارسال وقتی است
            // که کاربر واقعاً مکث کند، نه وقتی مرورگر تکه‌ای را
            // نهایی اعلام کند — وگرنه وسط جمله بریده می‌شود.
            if (this.silenceTimer) clearTimeout(this.silenceTimer);
            if (this.transcript.trim()) {
                this.silenceTimer = setTimeout(
                    () => this.flush(),
                    isMobileBrowser() ? MOBILE_SILENCE_MS : SILENCE_MS
                );
            }
        };

        instance.onerror = (event: any) => {
            const code = event?.error || "unknown";

            // خودمان قطع کرده‌ایم، یا کاربر حرفی نزده — هیچ‌کدام خطا نیست
            if (code === "aborted") return;
            if (code === "no-speech") {
                this.nextRestartMs = MOBILE_RESTART_MS;
                return;
            }

            if (code === "not-allowed" || code === "service-not-allowed") {
                this.active = false;
                if (this.handlers.onError) {
                    this.handlers.onError("اجازه‌ی دسترسی به میکروفون داده نشده است.");
                }
                this.emit("off");
                return;
            }

            this.failures += 1;
            if (this.failures >= MAX_FAILURES) {
                this.active = false;
                if (this.handlers.onError) {
                    this.handlers.onError("میکروفون پایدار نماند. یک بار دیگر روشنش کنید.");
                }
                this.emit("off");
            }
        };

        instance.onend = () => {
            this.running = false;

            // مرورگر نشست را بست. اگر حرفی مانده بود می‌فرستیم،
            // وگرنه بی‌سروصدا نشست تازه باز می‌کنیم.
            if (this.transcript.trim()) {
                this.flush();
                return;
            }
            this.scheduleRestart();
        };

        this.recognition = instance;

        try {
            instance.start();
        } catch {
            // معمولاً یعنی نمونه‌ی قبلی هنوز کامل بسته نشده
            this.failures += 1;
            this.running = false;
            if (this.failures < MAX_FAILURES) this.scheduleRestart();
            else if (this.handlers.onError) {
                this.handlers.onError("میکروفون باز نشد. یک بار دیگر امتحان کنید.");
            }
        }
    }
}
